import React, { memo } from 'react';
import { Box, Chip, Tooltip, Typography, alpha } from '@mui/material';
import { TaskAltRounded, ImageOutlined } from '@mui/icons-material';
import MathTextRenderer from './MathTextRenderer';

interface AnswerBadgeProps {
  answer?: string;
  answerImages?: string[];
  /** 20px chip for dense pickers; 22px elsewhere. */
  dense?: boolean;
}

const PREVIEW_CHARS = 160;

/**
 * A quiet marker that a question carries a sample answer and/or answer
 * images. Hovering shows the start of the answer. Renders nothing when the
 * question has neither.
 */
const AnswerBadge = memo<AnswerBadgeProps>(({ answer, answerImages, dense }) => {
  const text = (answer || '').trim();
  const images = answerImages?.length || 0;
  if (!text && !images) return null;

  const imagesLabel = `${images} image${images === 1 ? '' : 's'}`;
  const preview = text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}…` : text;

  return (
    <Tooltip
      arrow
      title={(
        <Box sx={{ maxWidth: 320, py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: text ? 0.25 : 0 }}>
            Sample answer{images ? ` · ${imagesLabel}` : ''}
          </Typography>
          {text && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.5 }}>
              <MathTextRenderer text={preview} />
            </Typography>
          )}
        </Box>
      )}
    >
      <Chip
        size="small"
        icon={<TaskAltRounded />}
        label={(
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Answer
            {images > 0 && (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, opacity: 0.8 }}>
                <ImageOutlined sx={{ fontSize: 13 }} />
                {images}
              </Box>
            )}
          </Box>
        )}
        sx={(theme) => ({
          height: dense ? 20 : 22,
          fontSize: '0.7rem',
          fontWeight: 600,
          color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
          bgcolor: alpha(theme.palette.success.main, 0.1),
          '& .MuiChip-icon': { fontSize: 14, color: 'inherit', ml: 0.75, mr: -0.25 },
          '& .MuiChip-label': { px: 0.75 },
        })}
      />
    </Tooltip>
  );
});

AnswerBadge.displayName = 'AnswerBadge';

export default AnswerBadge;
